/**
 * Canonical statement ordering for the stacked Provisional Financials page (RECUR-27640):
 * Balance Sheet -> Profit & Loss -> Cash Flow -> Other MIS, rendered as collapsible sections
 * regardless of the order `fetchMisTableOptions` returns them in.
 *
 * Mirrors auditedStatementTypes.ts deliberately, including WHY the match is by keyword and not
 * an exact string: the display string is backend-owned data (`misDisplayName`), so this module
 * must not hardcode it.
 *
 * `otherMis` is provisional-only — audited filters 'Other Metric (MIS)' out of its options
 * entirely. It also fetches differently (raw classification path, not the statement path) and
 * has no authorable layout, since its rows are per-org. See ProvisionalFinancials.tsx.
 */

export type ProvisionalStatementKey = 'balanceSheet' | 'profitAndLoss' | 'cashFlow' | 'otherMis';

/** Order matters — it is the render order of the sections. */
export const PROVISIONAL_STATEMENT_CONFIGS: { key: ProvisionalStatementKey; exact: RegExp; fallback: RegExp }[] = [
    { key: 'balanceSheet', exact: /^balance\s*sheet$/i, fallback: /\bbalance\s*sheet\b/i },
    { key: 'profitAndLoss', exact: /^profit\s*(and|&)\s*loss$/i, fallback: /\bprofit\s*(and|&)\s*loss\b/i },
    { key: 'cashFlow', exact: /^cash\s*flow$/i, fallback: /\bcash\s*flow\b/i },
    { key: 'otherMis', exact: /^other\s*metric\s*\(mis\)$/i, fallback: /\bother\s*metric\b/i },
];

/**
 * Resolves which backend `misDisplayName` backs each section.
 *
 * Exact match first, keyword only as a fallback (RECUR-27640). Provisional's `misOptions` is the
 * UNFILTERED MIS option set — unlike audited, which strips 'Other Metric (MIS)' and resolves against
 * three known statements — so it also carries per-org, user-authored MIS metric names. A bare
 * `/cash/i` or `/profit/i` would let an org-authored option sorting ahead of the real statement
 * silently retarget a whole section, and because the section title comes from the resolved name it
 * would look entirely plausible.
 *
 * The FALLBACK matches the full statement phrase, not a single word. A bare `/cash/i` still let
 * 'Monthly Cash Burn' win whenever the backend returned a non-exact official name such as
 * 'Cash Flow Statement' — the exact matcher missed, and the org-authored option simply sorted first.
 *
 * A statement already claimed by another section is never reused, so two sections cannot both
 * resolve to the same option.
 */
export const resolveStatementMetrics = (metricOptions: string[]): Record<ProvisionalStatementKey, string> => {
    const claimed = new Set<string>();
    const pick = (matcher: RegExp) => {
        const found = metricOptions.find((option) => matcher.test(option) && !claimed.has(option));
        if (found) claimed.add(found);
        return found;
    };
    return PROVISIONAL_STATEMENT_CONFIGS.reduce(
        (acc, { key, exact, fallback }) => {
            acc[key] = pick(exact) ?? pick(fallback) ?? '';
            return acc;
        },
        {} as Record<ProvisionalStatementKey, string>,
    );
};

/** Section-header fallback shown only until `fetchMisTableOptions` resolves the real name. */
export const PROVISIONAL_STATEMENT_FALLBACK_LABELS: Record<ProvisionalStatementKey, string> = {
    balanceSheet: 'Balance Sheet',
    profitAndLoss: 'Profit & Loss',
    cashFlow: 'Cash Flow',
    otherMis: 'Other MIS',
};
